/**
 * Game Assets - Centralized image URLs
 * 
 * Date: 2025-11-02 | Author: IA + Luiz Couto
 * 
 * All game assets hosted on Cloudflare Images CDN
 * Used across lobby and game screens for consistent visuals
 */

// Base CDN URL
const CDN_BASE = 'https://imagedelivery.net/nhaXXTkvgEPMwIHJ2G1pGQ';

/**
 * Table textures (for lobby table visualization)
 */
export const TABLE_TEXTURES = {
  /** Green felt table texture (bg01.jpg) */
  GREEN_FELT: `${CDN_BASE}/85d97acb-2974-4337-b1ad-482659d4dc00/public`,
} as const;

/**
 * Game table background textures (from NetCartas Buraco)
 * Date: 2025-11-02 | Author: IA + Luiz Couto
 * Used as full background in game screen
 */
export const GAME_TABLE_BACKGROUNDS = {
  /** Green felt texture */
  // FELT: `${CDN_BASE}/buracotranca/bg01.jpg/public`,
  FELT: `${CDN_BASE}/519167fc-57f2-4ce8-5b89-84aa0443ce00/public`,
  
  /** Wood texture */
  WOOD: `${CDN_BASE}/buracotranca/bg02.jpg/public`,
  
  /** Stone texture */
  STONE: `${CDN_BASE}/buracotranca/bg03.jpg/public`,
} as const;

export type TableBackgroundType = keyof typeof GAME_TABLE_BACKGROUNDS;

/**
 * Get random table background for game start
 * Date: 2025-11-02 | Author: IA + Luiz Couto
 */
export function getRandomTableBackground(): string {
  const keys = Object.keys(GAME_TABLE_BACKGROUNDS) as TableBackgroundType[];
  const randomKey = keys[Math.floor(Math.random() * keys.length)];
  return GAME_TABLE_BACKGROUNDS[randomKey];
}

/**
 * Empty chair images (no player seated)
 */
export const EMPTY_CHAIRS = {
  /** North position (top) */
  TOP: `${CDN_BASE}/a21154fd-4dd5-4627-0b34-cab6bf600a00/public`,
  
  /** East position (right) */
  RIGHT: `${CDN_BASE}/ee44a2b8-342b-402b-49d0-cab691b29d00/public`,
  
  /** South position (bottom) */
  BOTTOM: `${CDN_BASE}/e858c080-ee5c-4d44-46a8-f65950007a00/public`,
  
  /** West position (left) */
  LEFT: `${CDN_BASE}/e7298203-20e1-4c56-4a9c-bdf59369f100/public`,
} as const;

/**
 * Occupied chair images (with player seated)
 */
export const OCCUPIED_CHAIRS = {
  /** North position (top) with player */
  TOP: `${CDN_BASE}/fbcd010d-f47c-4c62-537b-67d3f12ff000/public`,
  
  /** East position (right) with player */
  RIGHT: `${CDN_BASE}/e046c14e-dd5b-4c26-4772-138fb1d41c00/public`,
  
  /** South position (bottom) with player */
  BOTTOM: `${CDN_BASE}/f2647bc2-2197-49a7-63c9-6e9c28baa400/public`,
  
  /** West position (left) with player */
  LEFT: `${CDN_BASE}/1598f0b2-606d-4927-9a6d-4c80c878a700/public`,
} as const;

/**
 * Get chair image URL based on position and occupation status
 * 
 * Date: 2025-11-02 | Author: IA + Luiz Couto
 * 
 * @param position - Seat position (top, right, bottom, left)
 * @param isOccupied - Whether the seat has a player
 * @returns Image URL for the chair
 */
export function getChairImage(
  position: 'top' | 'right' | 'bottom' | 'left',
  isOccupied: boolean
): string {
  const positionKey = position.toUpperCase() as keyof typeof EMPTY_CHAIRS;
  return isOccupied 
    ? OCCUPIED_CHAIRS[positionKey]
    : EMPTY_CHAIRS[positionKey];
}

